import { useMemo, useState } from 'react';
import { usersService, UserAdminView } from '../../services/users/users.service';
import { useAdminTenants, useAdminUsers } from '../../contexts/AdminEventsProvider';
import { INITIAL_FORM, type EditingBaseline, type UserFormState } from './users-page.types';
import {
  buildBaselineFromUser,
  buildCreatePayload,
  buildEditDraftFromUser,
  buildUpdatePayload,
  toggleTenantSelection,
} from './users-page.utils';

/**
 * Architectural summary: Encapsulates UsersPage state + action handlers
 * (modal flow, create/update, activate/deactivate, row expansion), while
 * relying on AdminEventsProvider for canonical users/tenants snapshots.
 */
export function useUsersPageController() {
  const {
    users,
    isLoading: isLoadingUsers,
    error: bootstrapError,
  } = useAdminUsers();
  // Architectural summary: Active-tenant choices come from the shared
  // AdminEventsProvider snapshot; this hook filters active tenants for modal
  // assignment chips and does not open any extra fetch/SSE channel.
  const { tenants: allTenants } = useAdminTenants();
  const tenants = useMemo(() => allTenants.filter((tenant) => tenant.isActive), [allTenants]);

  const [selectedTenantIds, setSelectedTenantIds] = useState<string[]>([]);
  const [form, setForm] = useState<UserFormState>(INITIAL_FORM);
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  // Architectural summary: Snapshot captured when edit modal opens; used to
  // build diff-only PATCH payloads on submit.
  const [editingBaseline, setEditingBaseline] = useState<EditingBaseline | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);

  const displayedError = error ?? bootstrapError;
  const usersCountLabel = useMemo(() => `סה"כ ${users.length} משתמשים`, [users.length]);
  const isEditing = editingUserId !== null;
  const modalTitle = isEditing ? 'עריכת משתמש' : 'צור משתמש חדש';
  const submitLabel = isEditing ? 'שמור' : 'צור';

  /**
   * Architectural summary: Toggles one tenant assignment in modal state.
   */
  const onTenantToggle = (tenantId: string) => {
    setSelectedTenantIds((previous) => toggleTenantSelection(previous, tenantId));
  };

  /**
   * Architectural summary: Restores create-mode draft and clears edit context.
   */
  const resetForm = () => {
    setForm(INITIAL_FORM);
    setSelectedTenantIds([]);
    setEditingUserId(null);
    setEditingBaseline(null);
  };

  /**
   * Architectural summary: Opens create modal with clean state.
   */
  const openCreateModal = () => {
    resetForm();
    setError(null);
    setIsModalOpen(true);
  };

  /**
   * Architectural summary: Opens edit modal prefilled from row + baseline
   * snapshot used by diff-only update payload creation.
   */
  const openEditModal = (user: UserAdminView) => {
    setError(null);
    setEditingUserId(user.id);
    setForm(buildEditDraftFromUser(user));
    const baseline = buildBaselineFromUser(user);
    setSelectedTenantIds(baseline.tenantIds);
    setEditingBaseline(baseline);
    setIsModalOpen(true);
  };

  /**
   * Architectural summary: Closes modal and clears draft/edit state.
   */
  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  /**
   * Architectural summary: Handles create and update submit paths with
   * validation and diff-only PATCH semantics.
   */
  const handleSubmitUser = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    if (selectedTenantIds.length === 0) {
      setError('יש לבחור לפחות חברה אחת');
      return;
    }

    setIsCreating(editingUserId === null);
    setIsSaving(editingUserId !== null);
    try {
      if (editingUserId !== null) {
        const updatePayload = buildUpdatePayload(form, selectedTenantIds, editingBaseline);
        if (Object.keys(updatePayload).length === 0) {
          closeModal();
          return;
        }
        await usersService.update(editingUserId, updatePayload);
      } else {
        await usersService.create(buildCreatePayload(form, selectedTenantIds));
      }
      closeModal();
    } catch (submitError: unknown) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : editingUserId !== null
            ? 'עדכון המשתמש נכשל'
            : 'יצירת המשתמש נכשלה',
      );
    } finally {
      setIsCreating(false);
      setIsSaving(false);
    }
  };

  /**
   * Architectural summary: Deactivates a user after confirmation; canonical
   * row status is applied by AdminEventsProvider SSE snapshot updates.
   */
  const deactivateUser = async (user: UserAdminView) => {
    setError(null);
    if (!window.confirm(`להשבית את המשתמש ${user.username}?`)) {
      return;
    }
    try {
      await usersService.deactivate(user.id);
    } catch (deactivateError: unknown) {
      setError(deactivateError instanceof Error ? deactivateError.message : 'השבתת המשתמש נכשלה');
    }
  };

  /**
   * Architectural summary: Reactivates a user after confirmation; canonical
   * row status is applied by AdminEventsProvider SSE snapshot updates.
   */
  const activateUser = async (user: UserAdminView) => {
    setError(null);
    if (!window.confirm(`להפעיל את המשתמש ${user.username}?`)) {
      return;
    }
    try {
      await usersService.activate(user.id);
    } catch (activateError: unknown) {
      setError(activateError instanceof Error ? activateError.message : 'הפעלת המשתמש נכשלה');
    }
  };

  /**
   * Architectural summary: Updates one key in the user form draft.
   */
  const onChangeForm = <K extends keyof UserFormState>(key: K, value: UserFormState[K]) => {
    setForm((previous) => ({ ...previous, [key]: value }));
  };

  /**
   * Architectural summary: Expands/collapses one table row in mobile mode.
   */
  const onToggleExpanded = (userId: number) => {
    setExpandedUserId((current) => (current === userId ? null : userId));
  };

  return {
    users,
    isLoadingUsers,
    tenants,
    selectedTenantIds,
    form,
    isCreating,
    isSaving,
    isModalOpen,
    expandedUserId,
    displayedError,
    usersCountLabel,
    isEditing,
    modalTitle,
    submitLabel,
    openCreateModal,
    openEditModal,
    closeModal,
    handleSubmitUser,
    deactivateUser,
    activateUser,
    onTenantToggle,
    onChangeForm,
    onToggleExpanded,
  };
}
